<template>
  <b-container class="square-terminal-add-location">
    <b-row
      v-if="!googleLoaded"
      class="mt-3"
    >
      <b-col>
        <loading-spinner>
          <div slot="title">
            Please wait loading Google address API
          </div>
        </loading-spinner>
      </b-col>
    </b-row>
    <template v-if="googleLoaded">
      <b-row class="mt-3">
        <b-col>
          <b-input-group prepend="Display Name *">
            <b-form-input
              v-model="displayname"
              data-cy="displayName"
              :disabled="inputDisabled"
              required
            />
          </b-input-group>
        </b-col>
      </b-row>

      <!-- Social Media Fields -->
      <b-row class="mt-3">
        <b-col md="6">
          <b-input-group prepend="Twitter Username">
            <b-form-input
              v-model="twitterUsername"
              data-cy="twitterUsername"
              :disabled="inputDisabled"
              placeholder="@username"
            />
          </b-input-group>
        </b-col>
        <b-col md="6">
          <b-input-group prepend="Instagram Username">
            <b-form-input
              v-model="instagramUsername"
              data-cy="instagramUsername"
              :disabled="inputDisabled"
              placeholder="@username"
            />
          </b-input-group>
        </b-col>
      </b-row>

      <b-row class="mt-3">
        <b-col>
          <b-input-group prepend="Facebook URL">
            <b-form-input
              v-model="facebookUrl"
              data-cy="facebookUrl"
              :disabled="inputDisabled"
              placeholder="https://facebook.com/yourpage"
            />
          </b-input-group>
        </b-col>
      </b-row>

      <!-- Location Type -->
      <b-row class="mt-3">
        <b-col>
          <label class="form-label"><strong>Location Type: *</strong></label>
          <b-form-radio-group
            v-model="locationType"
            data-cy="locationType"
            :disabled="inputDisabled"
            class="mt-2"
            required
          >
            <b-form-radio
              value="PHYSICAL"
              data-cy="locationType-PHYSICAL"
            >
              Physical Location
            </b-form-radio>
            <b-form-radio
              value="MOBILE"
              data-cy="locationType-MOBILE"
            >
              Mobile Location
            </b-form-radio>
          </b-form-radio-group>
        </b-col>
      </b-row>

      <!-- Business Hours -->
      <b-row class="mt-3">
        <b-col>
          <label class="form-label"><strong>Business Hours:</strong></label>
          <div class="mt-2">
            <business-hours-display
              v-model="businessHours"
              :disabled="processing"
              @editing-changed="onBusinessHoursEditingChanged"
            />
          </div>
        </b-col>
      </b-row>

      <b-row
        v-if="!enterManualAddress"
        class="mt-3 location-autocomplete"
      >
        <b-col>
          <b-input-group prepend="Location">
            <google-place-autocomplete
              v-if="$options.components.GooglePlaceAutocomplete"
              id="map"
              ref="address"
              data-cy="locationAutocomplete"
              classname="form-control"
              :fields="addressFields"
              :enable-geolocation="true"
              placeholder="Please enter terminal address"
              :country="countries"
              @placechanged="getAddressData"
            />
            <b-form-input
              v-else
              placeholder="Google Autocomplete not available in SSR mode - please use manual entry"
              disabled
            />
            <b-button
              :disabled="processing || businessHoursEditing"
              @click="enterAddressManually"
            >
              Manual
            </b-button>
          </b-input-group>
        </b-col>
      </b-row>
      <b-row
        v-if="selectedAddress && !enterManualAddress"
        class="mt-3"
      >
        <b-col data-cy="selectedAddress">
          <p><strong>Address details:</strong></p>
          {{ selectedAddress.street1 }}<br>
          {{ selectedAddress.city }}
          {{ selectedAddress.state }}
          {{ selectedAddress.postcode }}<br>
          {{ selectedAddressCountryName }}
        </b-col>
      </b-row>

      <b-row
        v-if="enterManualAddress"
        class="mt-3 manual-address"
      >
        <b-col>
          <b-input-group prepend="Address 1 *">
            <b-input
              v-model="address.street1"
              data-cy="manualAddressStreet1"
              :disabled="processing"
              required
            />
          </b-input-group>
          <b-input-group prepend="Address 2">
            <b-input
              v-model="address.street2"
              data-cy="manualAddressStreet2"
              :disabled="processing"
            />
          </b-input-group>
          <b-input-group prepend="City *">
            <b-input
              v-model="address.city"
              data-cy="manualAddressCity"
              :disabled="processing"
              required
            />
          </b-input-group>
          <b-input-group prepend="State *">
            <b-input
              v-model="address.state"
              data-cy="manualAddressState"
              :disabled="processing"
              required
            />
          </b-input-group>
          <b-input-group prepend="Postcode *">
            <b-input
              v-model="address.postcode"
              data-cy="manualAddressPostcode"
              :disabled="processing"
              required
            />
          </b-input-group>
          <b-input-group prepend="Country *">
            <b-input
              v-model="address.country"
              data-cy="manualAddressCountry"
              :disabled="processing"
              required
            />
          </b-input-group>
          <div class="address-mode-buttons">
            <b-button
              data-cy="saveManualAddress"
              variant="secondary"
              :disabled="processing || businessHoursEditing"
              @click="cancelEnterAddressManually"
            >
              Switch to Autocomplete
            </b-button>
          </div>
        </b-col>
      </b-row>

      <!-- Unified Save Location Button Section -->
      <b-row class="mt-4">
        <b-col>
          <div
            v-if="!canSaveLocation"
            class="validation-message mb-3"
          >
            <small class="text-muted">
              Please complete all required fields: Display Name, Location Type, and Address
            </small>
          </div>
          <div class="save-location-buttons">
            <b-button
              variant="secondary"
              :disabled="processing || businessHoursEditing"
              @click="cancelAddLocation"
            >
              Cancel
            </b-button>
            <b-button
              variant="primary"
              :disabled="!canSaveLocation || processing || businessHoursEditing"
              @click="saveLocation"
            >
              {{ saveButtonText }}
              <b-spinner
                v-if="processing"
                small
                class="ml-2"
              />
            </b-button>
          </div>
        </b-col>
      </b-row>
    </template>
  </b-container>
</template>

<script>
// import Vue from 'vue'
import BusinessHoursDisplay from './BusinessHoursDisplay.vue'
import AddressUtils from '../../mixins/address-utils.js'
// Import statically (a conditional require() of a .vue file is invisible to
// rollup and left unresolved in the dist bundle); importing an SFC is inert,
// so SSR/test safety lives where it always did -- in the conditional
// REGISTRATION below, and in the component's own window guards.
import GooglePlaceAutocompleteComponent from './GooglePlaceAutocomplete.vue'
const GooglePlaceAutocomplete = (process.env.NODE_ENV !== 'test' && typeof window !== 'undefined')
  ? GooglePlaceAutocompleteComponent
  : null

export default {
  name: 'square-terminal-list-add-location',
  components: {
    ...(GooglePlaceAutocomplete ? { GooglePlaceAutocomplete } : {}),
    BusinessHoursDisplay
  },
  mixins: [AddressUtils],
  props: {
    editMode: {
      type: Boolean,
      default: false
    },
    existingLocation: {
      type: Object,
      default: null
    }
  },
  data: function() {
    return {
      googleApiKey: 'AIzaSyAkfRZWVyxKvXvOpBFvTJiYSXAa-8rAmqs',
      googleLoaded: (window.google !== undefined),
      inputDisabled: false,
      displayname: '',
      processing: false,
      selectedAddress: null,
      enterManualAddress: false,
      countries: ['au', 'nz'],
      addressFields: [
        'address_components',
        'adr_address',
        'alt_id',
        'formatted_address',
        'geometry',
        'icon',
        'id',
        'name',
        'permanently_closed',
        'photo',
        'place_id',
        'scope',
        'type',
        'url',
        'utc_offset',
        'vicinity'
      ],
      address: {
        street1: '',
        street2: '',
        city: '',
        state: '',
        postcode: '',
        country: ''
      },
      // New fields
      twitterUsername: '',
      instagramUsername: '',
      facebookUrl: '',
      locationType: 'PHYSICAL', // Set default to PHYSICAL
      businessHours: [],
      businessHoursEditing: false // Track business hours editing state
    }
  },
  computed: {
    addressready: function() {
      return this.displayname.length && this.locationType && this.selectedAddress
    },
    manualAddressReady: function() {
      if (!this.displayname.length) return false
      if (!this.locationType) return false
      if (!this.address.street1.length) return false
      if (!this.address.city.length) return false
      if (!this.address.state.length) return false
      if (!this.address.postcode.length) return false
      if (!this.address.country.length) return false
      return true
    },
    // New computed property for unified save button
    canSaveLocation: function() {
      if (!this.displayname.length) return false
      if (!this.locationType) return false

      if (this.enterManualAddress) {
        return this.manualAddressReady
      }
      else {
        return this.addressready
      }
    },
    locations: function() {
      return this.$store.state.square.terminalLocations
    },
    selectedAddressCountryName() {
      // Use the countryName stored from Google Places API, or fall back to address-utils mapping
      if (this.selectedAddress && this.selectedAddress.countryName) {
        return this.selectedAddress.countryName
      }

      // Fallback to address-utils mapping if countryName not available
      const countryCode = this.selectedAddress && this.selectedAddress.country
      if (!countryCode) return ''

      const countryNames = {
        US: 'United States',
        CA: 'Canada',
        GB: 'United Kingdom',
        AU: 'Australia',
        JP: 'Japan',
        FR: 'France',
        DE: 'Germany',
        ES: 'Spain',
        IT: 'Italy',
        BR: 'Brazil',
        MX: 'Mexico',
        IN: 'India',
        CN: 'China',
        RU: 'Russia',
        KR: 'South Korea',
        NL: 'Netherlands',
        SE: 'Sweden',
        CH: 'Switzerland',
        NO: 'Norway',
        DK: 'Denmark',
        FI: 'Finland',
        BE: 'Belgium',
        AT: 'Austria',
        IE: 'Ireland',
        PL: 'Poland',
        PT: 'Portugal',
        CZ: 'Czech Republic',
        HU: 'Hungary',
        GR: 'Greece',
        TR: 'Turkey',
        IL: 'Israel',
        EG: 'Egypt',
        ZA: 'South Africa',
        NG: 'Nigeria',
        AR: 'Argentina',
        CL: 'Chile',
        CO: 'Colombia',
        PE: 'Peru',
        VE: 'Venezuela',
        TH: 'Thailand',
        MY: 'Malaysia',
        SG: 'Singapore',
        ID: 'Indonesia',
        PH: 'Philippines',
        VN: 'Vietnam',
        NZ: 'New Zealand'
      }
      return countryNames[countryCode] || countryCode
    },
    isEditMode() {
      return this.editMode && this.existingLocation
    },
    saveButtonText() {
      return this.isEditMode ? 'Update Location' : 'Save Location'
    }
  },
  mounted: function() {
    // Initialize Google Maps API
    if (window.google === undefined) {
      const googlePlaceScript = document.createElement('script')
      // loading=async is required for google.maps.importLibrary() to be
      // exposed — the new PlaceAutocompleteElement is fetched via that
      // modular entry point. v=weekly pins to the current release
      // channel where the element is generally available.
      const googlePlaceScriptURL = 'https://maps.googleapis.com/maps/api/js?key=' + this.googleApiKey + '&libraries=places&v=weekly&loading=async'
      googlePlaceScript.setAttribute('src', googlePlaceScriptURL)
      document.head.appendChild(googlePlaceScript)
      const self = this;

      (function checkMapsAPILoaded() {
        setTimeout(() => {
          if (window.google === undefined) {
            self.$nextTick(() => checkMapsAPILoaded())
          }
          else {
            self.googleLoaded = true
            self.initializeEditMode()
          }
        }, 250)
      })()
    }
    else {
      this.googleLoaded = true
      this.initializeEditMode()
    }
  },
  methods: {
    initializeEditMode() {
      if (this.isEditMode) {
        this.populateFieldsFromLocation()
      }
    },

    populateFieldsFromLocation() {
      if (!this.existingLocation) return

      const location = this.existingLocation

      // Populate basic fields - these are already in the correct format from the store
      this.displayname = location.name || ''
      this.locationType = location.type || 'PHYSICAL'
      this.twitterUsername = location.twitter_username || ''
      this.instagramUsername = location.instagram_username || ''
      this.facebookUrl = location.facebook_url || ''

      // Populate business hours - already mapped by the store actions
      if (location.business_hours &&
          Array.isArray(location.business_hours) &&
          location.business_hours.length > 0) {
        this.businessHours = [...location.business_hours] // Direct copy since already mapped
      }
      else {
        this.businessHours = []
      }

      // Populate address fields - already mapped by the store actions
      if (location.address) {
        this.enterManualAddress = true
        this.address = {
          street1: location.address.address_line_1 || '',
          street2: location.address.address_line_2 || '',
          city: location.address.locality || '',
          state: location.address.administrative_district_level_1 || '',
          postcode: location.address.postal_code || '',
          country: location.address.country || ''
        }
      }
    },

    generateUUID() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0
        const v = c === 'x' ? r : (r & 0x3 | 0x8)
        return v.toString(16)
      })
    },
    enterAddressManually: function() {
      this.enterManualAddress = true
    },
    cancelEnterAddressManually: function() {
      this.enterManualAddress = false
    },
    getAddressData: function(data, place, id) {
      let street1 = `${data.street_number} ${data.route}`
      if (data.subpremise !== undefined) {
        street1 = `${data.subpremise}/${street1}`
      }

      const findAddressValue = function(name, value) {
        const addrField = place.address_components.find(f => f.types.includes(name))
        if (addrField) {
          if (value === 'short') {
            return addrField.short_name
          }
          else {
            return addrField.long_name
          }
        }
        return ''
      }
      const address = {
        street1: street1,
        city: data.locality,
        state: data.administrative_area_level_1,
        country: findAddressValue('country', 'short'),
        countryName: findAddressValue('country', 'long'), // Store the full country name
        postcode: data.postal_code
      }

      this.$set(this, 'selectedAddress', address)
    },
    saveManualAddress: function() {
      this.storeNewAddress(this.address)
    },
    saveAddress: function() {
      this.storeNewAddress(this.selectedAddress)
    },
    cancelAddLocation: function() {
      this.$emit('cancel')
    },
    // New unified save location method
    saveLocation: function() {
      if (this.enterManualAddress) {
        this.storeNewAddress(this.address)
      }
      else {
        this.storeNewAddress(this.selectedAddress)
      }
    },
    storeNewAddress: function(address) {
      this.processing = true

      const updateAddress = {
        name: this.displayname,
        description: this.displayname,
        address_line_1: address.street1,
        address_line_2: address.street2 || '',
        city: address.city,
        state: address.state,
        postal_code: address.postcode,
        country: address.country,
        twitter_username: this.twitterUsername || null,
        instagram_username: this.instagramUsername || null,
        facebook_url: this.facebookUrl || null,
        type: this.locationType,
        business_hours: this.businessHours
      }

      const action = this.isEditMode ? 'square/updateLocation' : 'square/createLocation'
      const payload = this.isEditMode
        ? { locationId: this.existingLocation.id, ...updateAddress }
        : updateAddress

      this.$store.dispatch(action, payload)
        .then(response => {
          this.processing = false
          this.$emit('success', response)
        })
        .catch(err => {
          this.processing = false
          this.$emit('error', { location: { name: this.displayname, display_name: this.displayname }, error: err })
        })
    },
    onBusinessHoursEditingChanged(isEditing) {
      this.businessHoursEditing = isEditing
    }
  }
}
</script>

<style lang="scss" scoped>
.square-terminal-add-location {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  padding: 1.5rem;
  border-radius: 0.375rem;

  // Bootstrap Vue input group styling fixes with rounded corners
  ::v-deep .input-group {
    margin-bottom: 1rem;
    display: flex;
    position: relative;
    width: 100%;

    .input-group-prepend {
      display: flex;

      .input-group-text {
        min-width: 140px;
        text-align: left;
        background-color: #e9ecef;
        border: 1px solid #ced4da;
        border-right: 0;
        font-weight: 500;
        color: #495057;
        padding: 0.375rem 0.75rem;
        border-radius: 0.25rem 0 0 0.25rem;
        white-space: nowrap;
        display: flex;
        align-items: center;
        justify-content: flex-start;
      }
    }

    .form-control {
      border: 1px solid #ced4da;
      border-left: 0;
      background-color: #fff;
      padding: 0.375rem 0.75rem;
      border-radius: 0 0.25rem 0.25rem 0;
      flex: 1 1 auto;
      width: 1%;

      &:focus {
        border-color: #80bdff;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        background-color: #fff;
        outline: 0;
        z-index: 3;
      }

      &:disabled {
        background-color: #e9ecef;
        opacity: 1;
      }
    }

    .input-group-append {
      display: flex;

      .btn {
        border-radius: 0 0.25rem 0.25rem 0;
        border-left: 0;
        border: 1px solid #ced4da;
        z-index: 2;
      }
    }

    // Fix for single input without append
    &:not(.has-append) .form-control {
      border-left: 0;
      border-radius: 0 0.25rem 0.25rem 0;
    }
  }

  // Radio button styling
  ::v-deep .form-check {
    margin-bottom: 0.5rem;

    .form-check-input {
      margin-top: 0.3rem;
    }

    .form-check-label {
      font-weight: 400;
      color: #495057;
    }
  }

  // Button styling
  ::v-deep .btn {
    font-weight: 400;
    text-align: center;
    vertical-align: middle;
    border: 1px solid transparent;
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    line-height: 1.5;
    border-radius: 0.25rem;
    transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    &.btn-primary {
      color: #fff;
      background-color: #007bff;
      border-color: #007bff;

      &:hover:not(:disabled) {
        background-color: #0056b3;
        border-color: #004085;
      }

      &:disabled {
        opacity: 0.65;
        cursor: not-allowed;
      }
    }

    &.btn-secondary {
      color: #fff;
      background-color: #6c757d;
      border-color: #6c757d;

      &:hover:not(:disabled) {
        background-color: #545b62;
        border-color: #4e555b;
      }
    }
  }
}

.manual-address {
  ::v-deep .input-group {
    margin: 10px 0px;

    .input-group-prepend .input-group-text {
      min-width: 120px;
      text-align: left;
      background-color: #e9ecef;
      border-color: #ced4da;
      font-weight: 500;
      border-radius: 0.25rem 0 0 0.25rem;
    }
  }
}

.form-label {
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #495057;
  font-size: 14px;
}

.save-location-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #dee2e6;
}

.validation-message {
  text-align: center;

  small {
    color: #6c757d;
    font-style: italic;
  }
}

.address-mode-buttons {
  margin-top: 1rem;
  display: flex;
  justify-content: flex-end;
}

.location-autocomplete {
  position: relative;
  z-index: 1;
}
</style>
